import React, { useState, useEffect, useCallback } from 'react';
import { MenuItem, Rating, Typography } from '@mui/material';
import clsx from 'clsx';
import { FormMaterialSelector } from 'components/Selector';
import { useForm, Controller } from 'react-hook-form';
import Api from 'api';
import { Location } from 'components/BillingLocationSelector';
import { getSoftwareOptions } from 'utils/dentalSoftware';
import { StyledButton } from 'components/Buttons/DefaultButton/DefaultButton.styles';
import useActionApi from 'hooks/useActionApi';
import FixedLoader from 'components/FixedLoader/FixedLoader';
import styles from './SoftwareRatingModal.module.scss';

interface SoftwareRatingModalProps {
  location: Location;
  onSuccess: () => void;
  onClose: () => void;
}

export const SoftwareRatingModal: React.FC<SoftwareRatingModalProps> = ({ location, onSuccess, onClose }) => {
  const { control, watch, getValues } = useForm({
    defaultValues: {
      software: '',
      softwareRating: 0,
    },
  });

  const [isSuccess, setIsSuccess] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);

  const software = watch('software');
  const softwareRating = watch('softwareRating');
  const softwareOptions = getSoftwareOptions(location.country ?? 0);

  const [
    isLoading,
    error,
    updateLocation,
  ] = useActionApi({
    apiMethod: Api.updateLocation,
    onSuccess: () => setIsSuccess(true),
  });

  const onSubmit = useCallback((data: { software: string; softwareRating: number }) => {
    if (isLoading) return;
    updateLocation({
      locationId: location.id,
      data: {
        software: data.software,
        softwareRating: data.softwareRating,
      },
    });
  }, [isLoading, location.id, updateLocation]);

  useEffect(() => {
    if (software && validationError === 'Please select your office software') {
      setValidationError(null);
    }
  }, [software, validationError]);


  const handleSubmitClick = () => {
    const data = getValues();
    if (!data.software) {
      setValidationError('Please select your office software');
      return;
    }
    if (!data.softwareRating) {
      setValidationError('Please select your software satisfaction rating');
      return;
    }
    setValidationError(null);
    onSubmit(data);
  };

  if (isSuccess) {
    return (
      <div className={styles.container}>
        <Typography variant="h5" className={styles.title}>Thank You!</Typography>
        <Typography className={styles.subtitle}>
          We've updated your profile with your current software. Happy temping!
        </Typography>
        <StyledButton 
          onClick={onSuccess} 
          className={clsx(styles.button, 'no-fastclick')}
          variant="contained"
          color="primary"
        >
          Continue
        </StyledButton>
      </div>
    );
  }

  const isSubmitActive = software && softwareRating > 0;

  return (
    <div className={styles.container}>
      <FixedLoader loading={isLoading} />
      <Typography variant="h6" className={styles.title}>
        {location.practiceName} – to ensure you get the best matches, let's ensure your information is up-to-date.
      </Typography>

      <form>
        <div className={styles.formStep}>
          <Typography className={styles.label}>Select Your Practice Management Software:</Typography>
          <FormMaterialSelector
            control={control}
            name="software"
            label="- Select Your Software -"
          >
            {softwareOptions.map((softwareName) => (
              <MenuItem key={softwareName} value={softwareName}>
                {softwareName}
              </MenuItem>
            ))}
          </FormMaterialSelector>
        </div>

        {software && (
          <div className={styles.formStep}>
            <Typography className={styles.label}>How happy are you with {software}?</Typography>
            <Controller
              name="softwareRating"
              control={control}
              render={({ field }) => (
                <Rating
                  {...field}
                  className="no-fastclick"
                  onChange={(event, newValue) => {
                    field.onChange(newValue);
                    if (newValue && validationError === 'Please select your software satisfaction rating') {
                      setValidationError(null);
                    }
                  }}
                  size="large"
                />
              )}
            />
          </div>
        )}

        <div className={styles.buttonContainer}>
          <StyledButton
            onClick={handleSubmitClick}
            variant="contained"
            className={clsx('no-fastclick', { [styles.inactiveButton]: !isSubmitActive })}
          >
            Submit
          </StyledButton>
        </div>
        
        {validationError && <Typography color="error" className={styles.error}>{validationError}</Typography>}
        {error && <Typography color="error" className={styles.error}>{(error as any)?.message || 'An error occurred'}</Typography>}
      </form>
    </div>
  );
}; 